import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import axios from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const BuyerEmailVerificationPage = () => {
  const navigate = useNavigate();
  const { refreshCurrentUser } = useAuth();

  const [email, setEmail] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [otp, setOtp] = useState('');
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [info, setInfo] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const loadStatus = async () => {
      try {
        setLoadingStatus(true);
        setError('');
        const response = await axios.get('/api/email-verification/status');
        const status = response.data || {};
        setEmail(status.email || '');
        setEmailVerified(Boolean(status.email_verified));

        if (status.email_verified) {
          await refreshCurrentUser();
          navigate('/');
        } else {
          // Auto-send OTP when the page loads
          try {
            const otpResponse = await axios.post('/api/email-verification/send-otp');
            setInfo(otpResponse.data?.message || 'OTP sent to your email.');
          } catch {
            setError('Failed to send OTP automatically. Please use the button below.');
          }
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load email verification status');
      } finally {
        setLoadingStatus(false);
      }
    };

    loadStatus();
  }, [navigate, refreshCurrentUser]);

  const sendOtp = async () => {
    try {
      setSendingOtp(true);
      setError('');
      const response = await axios.post('/api/email-verification/send-otp');
      setInfo(response.data?.message || 'OTP sent to your email.');
    } catch (err) {
      setError('Error sendinng OTP. Please try after sometime.');
    } finally {
      setSendingOtp(false);
    }
  };

  const verifyOtp = async () => {
    if (!/^\d{4}$/.test(otp.trim())) {
      setError('Enter a valid 4-digit OTP.');
      return;
    }

    try {
      setVerifyingOtp(true);
      setError('');
      await axios.post('/api/email-verification/verify-otp', { otp: otp.trim() });
      setInfo('Email verified successfully. Redirecting...');
      await refreshCurrentUser();
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired OTP.');
    } finally {
      setVerifyingOtp(false);
    }
  };

  if (loadingStatus) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading verification status...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={7} lg={5}>
          <Card>
            <Card.Header className="text-center">
              <h4 className="mb-0">Verify Your Email</h4>
            </Card.Header>
            <Card.Body>
              <p className="text-muted mb-2">OTP has been sent to your email.</p>
              <p className="text-muted mb-2"><strong>{email || '-'}</strong></p>
              {emailVerified ? (
                <Alert variant="success" className="mb-0">Your email is already verified.</Alert>
              ) : (
                <>
                  {info && <Alert variant="info">{info}</Alert>}
                  {error && <Alert variant="danger">{error}</Alert>}

                  <Form.Group className="mb-3">
                    <Form.Label>Enter OTP</Form.Label>
                    <Form.Control
                      type="text"
                      value={otp}
                      onChange={(event) => setOtp(event.target.value)}
                      placeholder="Enter 4-digit OTP"
                      maxLength={4}
                    />
                  </Form.Group>

                  <div className="d-flex gap-2">
                    <Button variant="outline-secondary" onClick={sendOtp} disabled={sendingOtp || verifyingOtp}>
                      {sendingOtp ? 'Sending...' : 'Send OTP'}
                    </Button>
                    <Button variant="primary" onClick={verifyOtp} disabled={sendingOtp || verifyingOtp}>
                      {verifyingOtp ? 'Verifying...' : 'Verify Email'}
                    </Button>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default BuyerEmailVerificationPage;
